/**
 * VEDANGJYOTISHAM V6.x — SPIRITUALITY INTELLIGENCE VIEW
 * Master Component: Seeker (8 Concise Cards) & Acharya Deep Dive
 * Mode: Deterministic / Evidence-First / Canonical Palette Harmonized
 */

import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import {
  calculateSpiritualityIntelligence
} from '../../utils/v600/spiritualityIntelligenceEngine';
import {
  MasterSpiritualityResult,
  SeekerCardPresentation
} from '../../types/spiritualityIntelligence';
import {
  Sparkles,
  Compass,
  BookOpen,
  Calendar,
  Clock,
  Shield,
  Send,
  Heart,
  Target,
  Layers,
  ChevronDown,
  ChevronRight,
  Info,
  CheckCircle2,
  AlertTriangle,
  Flame,
  Sun,
  Activity,
  Award,
  Scale,
  User
} from 'lucide-react';

const CARD_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  card1_spiritualNature: Compass,
  card2_spiritualPath: Target,
  card3_spiritualPractice: Sun,
  card4_innerDevelopment: Award,
  card5_dharmaAndMoksha: Scale,
  card6_spiritualChallenges: Shield,
  card7_spiritualJourneyOverTime: Clock,
  card8_whatIsActiveNow: Flame
};

interface SpiritualityIntelligenceViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  focusDate?: Date | string;
  onOpenConsultation?: (prompt?: string) => void;
}

export const SpiritualityIntelligenceView: React.FC<SpiritualityIntelligenceViewProps> = ({
  kundali,
  lang = 'en',
  focusDate,
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';
  const [viewMode, setViewMode] = useState<'seeker' | 'acharya'>('seeker');
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // Compute Master Spirituality Intelligence
  const master: MasterSpiritualityResult = useMemo(() => {
    return calculateSpiritualityIntelligence(kundali, lang, { focusDate });
  }, [kundali, lang, focusDate]);

  const { evidenceObject, seeker, acharya } = master;
  const { spiritualityInclination, spiritualPath, currentSpiritualState } = evidenceObject;

  const handleConsultAcharya = (topic?: string) => {
    const prompt = topic
      ? `Guidance on my spiritual journey (${topic}) under ${currentSpiritualState.activeDashaPeriod} dasha.`
      : `Provide deep Vedic Acharya guidance on my Spiritual Path (${spiritualPath.pathTitleEn}), inclination (${spiritualityInclination.headlineEn}), and active timing window (${currentSpiritualState.activeDashaPeriod}).`;
    if (onOpenConsultation) {
      onOpenConsultation(prompt);
    }
  };

  const seekerCards: SeekerCardPresentation[] = [
    seeker.card1_spiritualNature,
    seeker.card2_spiritualPath,
    seeker.card3_spiritualPractice,
    seeker.card4_innerDevelopment,
    seeker.card5_dharmaAndMoksha,
    seeker.card6_spiritualChallenges,
    seeker.card7_spiritualJourneyOverTime,
    seeker.card8_whatIsActiveNow
  ];

  return (
    <div className="space-y-6 text-astro-text animate-in fade-in duration-200">
      {/* =================================================================== */}
      {/* 1. MASTER BANNER: SPIRITUALITY INTELLIGENCE & PRIMARY QUESTION     */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2">
            {/* Metadata Pills */}
            <div className="flex items-center gap-2 flex-wrap text-xs">
              {kundali.birthDetails?.name && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-astro-primary text-white text-xs font-bold border border-astro-border shadow-2xs">
                  <User className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{kundali.birthDetails.name}</span>
                  <span className="text-[10px] text-white/70 font-mono">
                    ({kundali.houses?.[0]?.sign || ''} Lagna • {kundali.planets?.find(p => p.name === 'Moon')?.sign || ''} Rasi)
                  </span>
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-astro-secondary-surface text-astro-primary text-xs font-bold border border-astro-border uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'अध्यात्म एवं साधना' : 'Spirituality Intelligence'}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-astro-sage-tint text-astro-primary text-xs font-bold border border-astro-primary/30">
                <Target className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? spiritualityInclination.headlineHi : spiritualityInclination.headlineEn}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-astro-secondary-surface text-astro-secondary-text text-xs font-medium border border-astro-border">
                <Clock className="w-3 h-3 text-astro-accent" />
                {currentSpiritualState.activeDashaPeriod}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-astro-secondary-surface text-astro-secondary-text text-xs font-medium border border-astro-border">
                <Calendar className="w-3 h-3 text-astro-muted" />
                {isHi ? `फोकस: ${currentSpiritualState.focusDate}` : `Focus: ${currentSpiritualState.focusDate}`}
              </span>
            </div>

            {/* Primary Question */}
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-astro-primary font-serif tracking-tight mt-1">
              {isHi ? 'मेरे लिए आध्यात्मिक मार्ग पर क्या संकेत हैं?' : 'What is there for me spiritually?'}
            </h1>
            <p className="text-xs sm:text-sm text-astro-secondary-text max-w-3xl leading-relaxed">
              {isHi
                ? 'आपकी कुंडली के आधार पर आध्यात्मिक अभिरुचि, आंतरिक विकास, साधना अनुकूलता एवं सत्य-अन्वेषण का शास्त्रीय परिप्रेक्ष्य।'
                : 'How your spiritual orientation, inner development and search for meaning may unfold through verified Parashari & Jaimini principles.'}
            </p>
          </div>

          {/* Action Button & Mode Switcher */}
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
            <button
              onClick={() => handleConsultAcharya()}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs shadow-xs transition-all cursor-pointer"
            >
              <Send className="w-3.5 h-3.5" />
              <span>{isHi ? 'आचार्य से आध्यात्मिक परामर्श' : 'Consult Acharya on Spirituality'}</span>
            </button>

            {/* Mode Switcher */}
            <div className="flex items-center p-1 rounded-xl bg-astro-secondary-surface border border-astro-border text-xs font-semibold">
              <button
                onClick={() => setViewMode('seeker')}
                className={`flex-1 px-3 py-1.5 rounded-lg transition-all cursor-pointer text-center ${
                  viewMode === 'seeker'
                    ? 'bg-astro-primary text-white font-bold shadow-xs'
                    : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
              >
                {isHi ? 'जिज्ञासु स्वरूप (Seeker)' : 'Seeker Mode'}
              </button>
              <button
                onClick={() => setViewMode('acharya')}
                className={`flex-1 px-3 py-1.5 rounded-lg transition-all cursor-pointer text-center ${
                  viewMode === 'acharya'
                    ? 'bg-astro-primary text-white font-bold shadow-xs'
                    : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
              >
                {isHi ? 'आचार्य प्रमाण (Forensics)' : 'Acharya Deep Dive'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 2. HUMAN AGENCY & SAFETY PRECAUTION BANNER                         */}
      {/* =================================================================== */}
      <div className="bg-astro-secondary-surface/70 border border-astro-border rounded-xl p-3.5 flex items-start gap-3 text-xs">
        <Shield className="w-4 h-4 text-astro-accent shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <span className="font-bold text-astro-primary">
            {isHi ? 'शास्त्रीय मर्यादा एवं मानवीय स्वतंत्रता:' : 'Vedic Principle of Human Agency:'}
          </span>
          <p className="text-astro-secondary-text leading-relaxed">
            {isHi
              ? 'ज्योतिष किसी को सन्यासी या मुक्त घोषित नहीं करता; यह आपकी स्वाभाविक प्रवृत्तियों एवं साधना की अनुकूलता को दर्शाता है। निर्णय एवं आचरण पूर्णतः आपका है।'
              : 'VedangJyotisham informs tendencies and timings without making dogmatic claims of enlightenment or forced renunciation. True spirituality is lived with agency in everyday life.'}
          </p>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 3. SEEKER VIEW: 8 CONCISE, NON-TECHNICAL CARDS                      */}
      {/* =================================================================== */}
      {viewMode === 'seeker' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {seekerCards.map((card) => {
            const isExpanded = expandedCardId === card.cardId;
            const CardIcon = CARD_ICONS[card.cardId] || Sparkles;
            return (
              <div
                key={card.cardId}
                className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-3.5 transition-all hover:border-astro-accent/50 flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 border-b border-astro-border/70 pb-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-astro-secondary-surface border border-astro-border flex items-center justify-center shrink-0 shadow-2xs">
                        <CardIcon className="w-4 h-4 text-astro-accent" />
                      </div>
                      <div>
                        <span className="text-[11px] font-bold text-astro-primary uppercase tracking-wider block">
                          {isHi ? card.titleHi : card.titleEn}
                        </span>
                        {card.sanskritTerm && (
                          <span className="text-[10px] font-mono text-astro-muted">
                            {card.sanskritTerm}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs font-serif font-bold text-astro-accent bg-astro-secondary-surface px-2.5 py-0.5 rounded border border-astro-border shrink-0">
                      {isHi ? card.headlineHi : card.headlineEn}
                    </span>
                  </div>

                  <p className="text-xs text-astro-secondary-text leading-relaxed">
                    {isHi ? card.bodyHi : card.bodyEn}
                  </p>

                  {/* Bullet Points */}
                  {card.bulletPointsEn && card.bulletPointsEn.length > 0 && (
                    <ul className="space-y-1.5 text-xs text-astro-text pt-1">
                      {(isHi ? card.bulletPointsHi || card.bulletPointsEn : card.bulletPointsEn).map((pt, idx) => (
                        <li key={idx} className="flex items-start gap-2 bg-astro-secondary-surface/70 p-2 rounded-lg border border-astro-border/50">
                          <CheckCircle2 className="w-3.5 h-3.5 text-astro-primary shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Footer Evidence Summary */}
                <div className="pt-2 border-t border-astro-border/70 flex items-center justify-between text-[11px] text-astro-muted">
                  <div className="flex items-center gap-1">
                    <Info className="w-3 h-3 text-astro-accent" />
                    <span>{isHi ? card.evidenceSummaryHi : card.evidenceSummaryEn}</span>
                  </div>
                  <button
                    onClick={() => handleConsultAcharya(card.titleEn)}
                    className="text-astro-primary hover:text-astro-accent font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <span>{isHi ? 'परामर्श' : 'Inquire'}</span>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =================================================================== */}
      {/* 4. ACHARYA DEEP DIVE: FORENSIC CLASSICAL EVIDENCE                   */}
      {/* =================================================================== */}
      {viewMode === 'acharya' && (
        <div className="space-y-6">
          {/* Section A: D1 Bhava & Graha Foundation Table */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-astro-accent" />
                <h3 className="text-sm font-bold text-astro-text font-serif">
                  {isHi ? '१. भाव व भावेश शास्त्रीय संमिलन (Dharma & Moksha Bhavas)' : '1. Classical Dharma & Moksha Bhava Analysis'}
                </h3>
              </div>
              <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-astro-secondary-surface text-astro-primary border border-astro-border">
                BPHS & Saravali Standards
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {acharya.d1BhavaFactors.map((bhava) => (
                <div key={bhava.bhavaNum} className="p-3.5 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-astro-primary">
                      {isHi ? `${bhava.bhavaNum}वां भाव` : `House ${bhava.bhavaNum}`} ({bhava.sign})
                    </span>
                    <span className="font-mono text-astro-secondary-text">
                      {isHi ? `स्वामी: ${bhava.lord}` : `Lord: ${bhava.lord}`}
                    </span>
                  </div>
                  <p className="text-xs text-astro-secondary-text leading-relaxed">
                    {bhava.shastricAssessment}
                  </p>
                  <div className="text-[11px] text-astro-muted flex items-center justify-between pt-1 border-t border-astro-border/60">
                    <span>{isHi ? 'स्थित ग्रह:' : 'Occupants:'} {bhava.occupants.length > 0 ? bhava.occupants.join(', ') : (isHi ? 'कोई नहीं' : 'None')}</span>
                    <span>{isHi ? 'दृष्टि:' : 'Aspects:'} {bhava.aspects.join(', ')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section B: Karakas & D-20 Vimsamsha Upasana Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Karakas Box */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                <Sparkles className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'जैमिनी कारक एवं कारकांश विवेचन' : 'Jaimini Karakas & Karakamsha Sthiti'}</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="p-3 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-1">
                  <div className="flex justify-between font-bold text-astro-text">
                    <span>{isHi ? 'आत्मकारक (AK):' : 'Atmakaraka (AK):'} {acharya.karakasAnalysis.ak}</span>
                    <span className="text-astro-primary">House {acharya.karakasAnalysis.akHouse}</span>
                  </div>
                  <p className="text-astro-secondary-text">
                    {isHi ? `नवांश कारकांश: ${acharya.karakasAnalysis.karakamshaSign}` : `Navamsha Karakamsha: ${acharya.karakasAnalysis.karakamshaSign}`}
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-1">
                  <div className="flex justify-between font-bold text-astro-text">
                    <span>{isHi ? 'भ्रातृकारक (BK - गुरु कारक):' : 'Bhratrikaraka (BK - Guru Signifier):'} {acharya.karakasAnalysis.bk}</span>
                    <span className="text-astro-primary">House {acharya.karakasAnalysis.bkHouse}</span>
                  </div>
                  <p className="text-astro-secondary-text">
                    {acharya.karakasAnalysis.ishtaDevataFactor}
                  </p>
                </div>
              </div>
            </div>

            {/* D-20 Vimsamsha Box */}
            <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                <Layers className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'विंशांश चक्र (D-20) उपासना संपुष्ट' : 'D-20 Vimsamsha Upasana Analysis'}</span>
              </div>
              <div className="space-y-2 text-xs">
                {acharya.d20VimsamshaFactors.map((vf, idx) => (
                  <div key={idx} className="p-2.5 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-1">
                    <div className="flex justify-between font-bold text-astro-text">
                      <span>{vf.factorName}</span>
                      <span className="text-astro-accent font-mono">{vf.factorPlacement}</span>
                    </div>
                    <p className="text-astro-secondary-text text-[11px]">{vf.classicalSignificance}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section C: Shastric Citations & Shlokas */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-astro-accent" />
              <h3 className="text-sm font-bold text-astro-text font-serif">
                {isHi ? 'शास्त्रीय प्रमाण श्लोक एवं व्यावहारिक अनुप्रयोग' : 'Classical Shastric Citations & Vedic Applications'}
              </h3>
            </div>
            <div className="space-y-3">
              {acharya.shastricCitations.map((cit, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
                    <span>{cit.shastra} ({cit.slokaRef})</span>
                    <span className="text-[10px] text-astro-muted font-mono uppercase">Verified Canon</span>
                  </div>
                  <div className="font-mono text-xs text-astro-text bg-astro-card p-2.5 rounded border border-astro-border font-semibold">
                    {cit.excerpt}
                  </div>
                  <p className="text-xs text-astro-secondary-text leading-relaxed">
                    {cit.application}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Section D: Live Panchanga & Observance Grounding */}
          {acharya.panchangaProvenance && (
            <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Sun className="w-4 h-4 text-astro-accent" />
                  <h3 className="text-sm font-bold text-astro-text font-serif">
                    {isHi ? '४. तात्कालिक पंचांग एवं साधना-काल संमिलन' : '4. Live Panchanga & Observance Provenance'}
                  </h3>
                </div>
                <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-astro-secondary-surface text-astro-primary border border-astro-border">
                  {acharya.panchangaProvenance.samvatSummary}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="p-3 rounded-xl bg-astro-secondary-surface border border-astro-border">
                  <span className="text-[10px] text-astro-muted block uppercase tracking-wider">{isHi ? 'तिथि' : 'Tithi'}</span>
                  <span className="font-bold text-astro-text">{acharya.panchangaProvenance.tithiName}</span>
                  <span className="text-[11px] text-astro-secondary-text block">{acharya.panchangaProvenance.tithiPaksha}</span>
                </div>
                <div className="p-3 rounded-xl bg-astro-secondary-surface border border-astro-border">
                  <span className="text-[10px] text-astro-muted block uppercase tracking-wider">{isHi ? 'नक्षत्र' : 'Nakshatra'}</span>
                  <span className="font-bold text-astro-text">{acharya.panchangaProvenance.nakshatraName}</span>
                </div>
                <div className="p-3 rounded-xl bg-astro-secondary-surface border border-astro-border">
                  <span className="text-[10px] text-astro-muted block uppercase tracking-wider">{isHi ? 'योग' : 'Yoga'}</span>
                  <span className="font-bold text-astro-text">{acharya.panchangaProvenance.yogaName}</span>
                </div>
                <div className="p-3 rounded-xl bg-astro-secondary-surface border border-astro-border">
                  <span className="text-[10px] text-astro-muted block uppercase tracking-wider">{isHi ? 'सक्रिय पर्व/व्रत' : 'Active Parva/Vrat'}</span>
                  <span className="font-bold text-astro-accent">
                    {(isHi ? acharya.panchangaProvenance.activeObservancesHi : acharya.panchangaProvenance.activeObservances).length > 0
                      ? (isHi ? acharya.panchangaProvenance.activeObservancesHi : acharya.panchangaProvenance.activeObservances).join(', ')
                      : (isHi ? 'दैनिक नित्य साधना' : 'Daily Nitya Sadhana')}
                  </span>
                </div>
              </div>
              <p className="text-xs text-astro-secondary-text p-3 rounded-xl bg-astro-secondary-surface/80 border border-astro-border">
                <span className="font-bold text-astro-primary">{isHi ? 'साधना समन्वय: ' : 'Sadhana Alignment: '}</span>
                {isHi ? acharya.panchangaProvenance.dailySadhanaAlignmentHi : acharya.panchangaProvenance.dailySadhanaAlignmentEn}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
